import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../services/api';
import { Bell, Check, CheckCheck, MessageSquare, UserPlus, Clock, AlertTriangle, TrendingUp } from 'lucide-react';

const typeIcons = {
  task_assigned: <UserPlus className="w-4 h-4 text-blue-500" />,
  task_status_changed: <Check className="w-4 h-4 text-green-500" />,
  comment_added: <MessageSquare className="w-4 h-4 text-purple-500" />,
  deadline_approaching: <Clock className="w-4 h-4 text-orange-500" />,
  task_overdue: <AlertTriangle className="w-4 h-4 text-red-500" />,
  lead_followup: <TrendingUp className="w-4 h-4 text-cyan-500" />,
};

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsApi.getAll({ limit: 30 });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Also trigger deadline check on mount
    notificationsApi.checkDeadlines().catch(() => {});
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await notificationsApi.markRead(notif.id);
        setNotifications(notifications.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    // Navigate to entity
    if (notif.entity_type === 'task') navigate('/tasks');
    else if (notif.entity_type === 'project') navigate(`/projects/${notif.entity_id}`);
    else if (notif.entity_type === 'lead') navigate(`/leads/${notif.entity_id}`);
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="p-2 text-navy/60 hover:text-navy relative"
        onClick={() => setOpen(!open)}
        data-testid="notifications-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] bg-white rounded-xl shadow-xl border border-navy/10 z-50 overflow-hidden" data-testid="notifications-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy/10">
            <h3 className="font-semibold text-[#1A2B5E] text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-[#106EFD] hover:underline flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 mx-auto text-navy/20 mb-2" />
                <p className="text-sm text-navy/40">No notifications yet</p>
              </div>
            ) : notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors border-b border-navy/5 ${!notif.read ? 'bg-blue-50/40' : ''}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {typeIcons[notif.type] || <Bell className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notif.read ? 'font-medium text-[#1A2B5E]' : 'text-navy/70'}`}>
                    {notif.title}
                  </p>
                  <p className="text-xs text-navy/50 mt-0.5 line-clamp-2">{notif.message}</p>
                  <p className="text-[10px] text-navy/30 mt-1">{timeAgo(notif.created_at)}</p>
                </div>
                {!notif.read && <div className="w-2 h-2 rounded-full bg-[#106EFD] flex-shrink-0 mt-2" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
